import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Pencil, Syringe } from "lucide-react";
import { IconButton } from "@akuna/ui";
import { VaccinationFormDialog } from "@/components/applications/ApplicationFormDialog";
import { cn } from "@/lib/utils";
import { petReservationSummaryKey, type PetReservationSummary } from "../usePetReservationSummary";
import { PetProfileSectionEmpty, PetProfileSectionShell } from "../PetProfileSectionShell";

function formatApplicationDate(value: string, language: string) {
  const calendarDate = value.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  const date = new Date(calendarDate ? `${calendarDate}T12:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(language, { day: "2-digit", month: "2-digit", year: "numeric" }).format(date);
}

export type PetApplicationsSectionProps = {
  petId: string;
  applications: PetReservationSummary["applications"];
  compact?: boolean;
  showEmpty?: boolean;
};

export function PetApplicationsSection({ petId, applications, compact, showEmpty }: PetApplicationsSectionProps) {
  const { t, i18n } = useTranslation("daycareHotel");
  const queryClient = useQueryClient();
  const language = i18n?.resolvedLanguage ?? i18n?.language ?? "pt-BR";

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const pending = applications.filter((application) => application.status !== "completed");
  const editing = pending.find((application) => application.id === editingId) ?? null;
  const isPlan = editing?.origin === "plan";

  if (compact && !showEmpty && pending.length === 0) return null;

  const refresh = () => queryClient.invalidateQueries({ queryKey: petReservationSummaryKey(petId) });

  return (
    <PetProfileSectionShell
      title={t("petProfile.applications")}
      icon={Syringe}
      tone="success"
      count={pending.length}
      fullWidth
      onAdd={() => {
        setEditingId(null);
        setDialogOpen(true);
      }}
      addLabel={t("petProfile.addApplication")}
      compact={compact}
    >
      {pending.length === 0 ? (
        <PetProfileSectionEmpty>{t("petProfile.applicationsEmpty")}</PetProfileSectionEmpty>
      ) : (
        <ul className={cn(compact ? "flex flex-wrap gap-1.5" : "grid gap-2 sm:grid-cols-2")}>
          {pending.map((application) => {
            const overdue = application.status === "overdue";
            return (
              <li
                key={application.id}
                className={cn(
                  "group flex min-w-0 items-center gap-1.5 border",
                  compact ? "max-w-full rounded-full py-1 pl-2.5 pr-1" : "rounded-lg py-1 pl-2.5 pr-1",
                  overdue
                    ? "border-state-danger/30 bg-state-danger-soft/40"
                    : "border-state-warning/30 bg-state-warning-soft/40",
                )}
              >
                <div className="min-w-0 flex-1 text-xs">
                  <div className="flex min-w-0 flex-wrap items-center gap-1">
                    <span className="truncate font-medium">
                      {application.name ?? t("petProfile.applicationFallback")}
                    </span>
                    <span
                      className={cn(
                        "rounded-full border px-1.5 py-0 text-[10px] font-medium leading-4",
                        overdue
                          ? "border-state-danger/30 bg-state-danger-soft text-state-danger"
                          : "border-state-warning/30 bg-state-warning-soft text-state-warning-foreground",
                      )}
                    >
                      {t(overdue ? "petProfile.applicationOverdueBadge" : "petProfile.applicationUpcomingBadge")}
                    </span>
                  </div>
                  {application.nextDoseDate ? (
                    <span
                      className={cn(
                        "flex items-center gap-0.5",
                        overdue ? "text-state-danger-foreground" : "text-state-warning-foreground",
                      )}
                    >
                      <CalendarClock className="h-3 w-3 shrink-0" aria-hidden="true" />
                      {t("petProfile.applicationDueDate", {
                        date: formatApplicationDate(application.nextDoseDate, language),
                      })}
                    </span>
                  ) : null}
                </div>
                <IconButton
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
                  label={t("petProfile.editApplication")}
                  onClick={() => {
                    setEditingId(application.id);
                    setDialogOpen(true);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                </IconButton>
              </li>
            );
          })}
        </ul>
      )}

      <VaccinationFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingId(null);
        }}
        petId={petId}
        clinicId={editing?.clinicId ?? undefined}
        vaccination={
          editing && !isPlan
            ? {
                id: editing.id,
                item_id: editing.itemId,
                next_application_item_id: editing.nextApplicationItemId,
                item: editing.item,
                next_application_item: editing.nextApplicationItem,
                application_date: editing.applicationDate,
                expiry_date: editing.expiryDate,
                next_dose_date: editing.nextDoseDate,
                has_next_dose: editing.hasNextDose,
                clinic_id: editing.clinicId,
                appointment_id: editing.appointmentId,
                medical_record_id: editing.medicalRecordId,
              }
            : null
        }
        plan={
          editing && isPlan
            ? {
                id: editing.id,
                item_id: editing.itemId ?? "",
                due_date: editing.nextDoseDate ?? editing.applicationDate,
                notes: editing.notes,
                appointment_id: editing.appointmentId,
                source_type: editing.sourceType ?? "manual",
                source_pet_application_id: editing.sourcePetApplicationId,
              }
            : null
        }
        initialTab={isPlan ? "plan" : "record"}
        onApplicationSaved={refresh}
        onApplicationDeleted={refresh}
        onPlanSaved={refresh}
      />
    </PetProfileSectionShell>
  );
}
